/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-01-22 18:38:41                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 10:53:48                               *
 *****************************************************************************/

import { t } from "../../../i18n";
import { isOdooConnected } from "../../odoo/odoo";

/* SUMMARY
  * customerExportDescriptor: describe data mapping to csv
*/

/**
* customerExportDescriptor: describe data mapping to csv
*/
export const customerExportDescriptor = [{
  column: "id",
  columnTranslationKey: "common:export.columns.id",
  // Identifiant technique, demandé pour rapprocher un export d'un autre système. Décoché comme
  // n'importe quelle autre colonne si le lecteur n'en a pas l'usage.
  getter: (e: { id?: string }) => e.id || ""
}, {
  column: "ref",
  columnTranslationKey: "common:ref.label",
  // Les clients étaient la seule des cinq vues à n'exporter aucune référence métier, alors que le
  // champ existe et que les interventions, visites et pièces exportent la leur.
  getter: (data: EasyTech.Customer) => data.ref || ""
}, {
  column: "name",
  columnTranslationKey: "common:name",
  getter: (data: EasyTech.Customer) => data.name
}, {
  column: "activity",
  columnTranslationKey: "common:activitySector",
  getter: (data: EasyTech.Customer) => {
    if (!data.activitySector) return "";
    if (typeof data.activitySector === "string") return data.activitySector;
    if (typeof data.activitySector?.label === "string") return data.activitySector.label;
    return "";
  }
}, {
  column: "nip",
  columnTranslationKey: "common:nip",
  getter: (data: EasyTech.Customer) => data.nip
}, {
  column: "email",
  columnTranslationKey: "common:email",
  getter: (data: EasyTech.Customer) => data.contactInfo?.email || t("common:noEmail")
}, {
  column: "phone",
  columnTranslationKey: "common:phone",
  getter: (data: EasyTech.Customer) => data.contactInfo?.phone || t("common:noPhone")
}, {
  column: "fax",
  columnTranslationKey: "common:fax",
  getter: (data: EasyTech.Customer) => data.contactInfo?.fax || t("common:noFax")
}, {
  column: "bad payer",
  columnTranslationKey: "common:badPayer",
  getter: (data: EasyTech.Customer) => data.badPayer ? "true" : "false"
}, {
  column: "status",
  columnTranslationKey: "common:status",
  // Colonne « Statut » de la table clients. L'export des visites la produisait déjà pour le client
  // rattaché, mais avec un switch renvoyant de l'anglais en dur ; ici on passe par les clés que le
  // sélecteur de type utilise, donc le fichier suit la langue comme le reste.
  getter: (data: EasyTech.Customer) =>
    data.type ? t(`client:type.${String(data.type).toLowerCase()}`) : ""
}, {
  column: "address line 1",
  columnTranslationKey: "common:export.columns.addressLine1",
  getter: (data: EasyTech.Customer) => data.address?.address || ""
}, {
  column: "address line 2",
  columnTranslationKey: "common:export.columns.addressLine2",
  getter: (data: EasyTech.Customer) => data.address?.addressCompl || ""
}, {
  column: "zip code",
  columnTranslationKey: "common:export.columns.zipCode",
  getter: (data: EasyTech.Customer) => data.address?.zipCode || ""
}, {
  column: "city",
  columnTranslationKey: "common:export.columns.city",
  getter: (data: EasyTech.Customer) => data.address?.city || ""
}, {
  column: "country",
  columnTranslationKey: "common:export.columns.country",
  getter: (data: EasyTech.Customer) => data.address?.country || ""
}, {
  column: "lat",
  columnTranslationKey: "common:export.columns.latitude",
  getter: (data: EasyTech.Customer) => data.address?.latitude ?? ""
}, {
  column: "long",
  columnTranslationKey: "common:export.columns.longitude",
  getter: (data: EasyTech.Customer) => data.address?.longitude ?? ""
}, {
  column: "notes",
  columnTranslationKey: "common:notes",
  getter: (data: EasyTech.Customer) => {
    if (!data.notes) return "";
    return data.notes.replace(/\r?\n/g, " ").replace(/\r/g, " ");
  }
}];
/***/

/**
* odooExternalIdColumn: l'identifiant Odoo du client.
*
* Séparé du descripteur de base parce qu'il n'a de sens que sur un realm relié à Odoo : ailleurs la
* colonne serait vide sur toutes les lignes, et une colonne systématiquement vide dans la liste des
* champs à cocher se lit comme une donnée manquante plutôt que comme une donnée sans objet.
*/
const odooExternalIdColumn: EasyTech.ExportDescriptor = {
  column: "odoo external id",
  columnTranslationKey: "common:export.columns.odooExternalId",
  // `externalId` est typé `number | string` : un identifiant se lit, ne se calcule pas, donc on
  // écrit du texte plutôt que de laisser le tableur en faire un nombre et manger un zéro de tête.
  getter: (data: EasyTech.Customer) =>
    data.externalId === undefined || data.externalId === null ? "" : String(data.externalId)
};

/**
* getCustomerExportDescriptor: le descripteur clients adapté au realm courant.
*
* Une fonction plutôt qu'une constante, contrairement aux cinq autres descripteurs : la disponibilité
* d'Odoo dépend du realm, lu dans le stockage local, et une constante évaluée à l'import figerait la
* réponse au chargement de la page.
*/
export const getCustomerExportDescriptor = (): EasyTech.ExportDescriptor[] =>
  isOdooConnected()
    ? [...customerExportDescriptor, odooExternalIdColumn]
    : customerExportDescriptor;
/***/
